import React, { useEffect } from 'react'
import moment from 'moment'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { CHARACTERISTICS, statusColor } from '../incidentConstants'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

const StatTile = ({ label, value, caption, tone }) => (
  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 0 }}>
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {tone && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: `${tone}.main`,
            flexShrink: 0,
          }}
        />
      )}
      <Typography sx={{ fontWeight: 800, fontSize: '1rem' }} noWrap>
        {value || '—'}
      </Typography>
    </Stack>
    {caption && (
      <Typography
        component="div"
        variant="caption"
        color="text.secondary"
        noWrap
      >
        {caption}
      </Typography>
    )}
  </Card>
)

// Page header for a single incident: identity, live status, reporter and an
// at-a-glance tile row. Editing happens in the tabs below — the hero reflects
// unsaved edits live because it renders from the same incident state.
const IncidentHero = ({ incident, handlePDFDownload }) => {
  const activeCharacteristics = CHARACTERISTICS.filter(
    (characteristic) => incident?.[characteristic.key],
  )
  const { setPageTitle } = useDocumentTitle()
  useEffect(() => {
    if (incident?.incidentNumber) {
      setPageTitle(`Incident #${incident.incidentNumber}`)
    }
  }, [incident?.incidentNumber, setPageTitle])

  return (
    <Paper sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(211,47,47,0.10)',
              color: 'error.main',
              flexShrink: 0,
            }}
          >
            <ReportProblemOutlinedIcon />
          </Box>
          <Box minWidth={0} flex={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="title2" fontSize={26} padding={0}>
                Incident #{incident?.incidentNumber}
              </Typography>
              <Chip
                size="small"
                label={incident?.status ?? 'New'}
                color={statusColor(incident?.status)}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
              {incident?.incidentType && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={incident.incidentType}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {activeCharacteristics.map((characteristic) => (
                <Chip
                  key={characteristic.key}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={characteristic.label}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              mt={0.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Avatar
                src={incident?.reportedBy?.photograph}
                sx={{ width: 20, height: 20 }}
              />
              <Typography variant="caption" color="text.secondary">
                Reported by {incident?.reportedBy?.firstName}{' '}
                {incident?.reportedBy?.lastName}
                {incident?.createdAt &&
                  ` on ${moment(incident.createdAt).format('Do MMM YYYY [at] HH:mm')}`}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePDFDownload}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>

        <Box
          display="grid"
          gap={1.5}
          gridTemplateColumns={{
            xs: '1fr 1fr',
            sm: '1fr 1fr 1fr',
            lg: 'repeat(5, 1fr)',
          }}
        >
          <StatTile
            label="Incident Date"
            value={
              incident?.incidentDate
                ? moment(incident.incidentDate).format('Do MMM YYYY')
                : null
            }
            caption={
              incident?.incidentDate
                ? moment(incident.incidentDate).format('HH:mm')
                : null
            }
          />
          <StatTile label="Location" value={incident?.incidentLocation} />
          <StatTile
            label="Harm Result"
            value={incident?.result}
            caption={incident?.actualImpact}
            tone={
              incident?.result === 'Harm'
                ? 'error'
                : incident?.result === 'Near Miss'
                  ? 'warning'
                  : incident?.result === 'No Harm'
                    ? 'success'
                    : null
            }
          />
          <StatTile
            label="Assigned To"
            value={
              incident?.responsibleManager?.userAccount
                ? `${incident.responsibleManager.userAccount.firstName} ${incident.responsibleManager.userAccount.lastName}`
                : 'Unassigned'
            }
            tone={incident?.responsibleManager ? 'success' : 'warning'}
          />
          <StatTile
            label="Witness Statements"
            value={`${incident?.witnessStatements?.length ?? 0}`}
            caption={
              incident?.status === 'Awaiting Witness'
                ? 'Awaiting an external witness'
                : `${incident?.files?.length ?? 0} attachment${
                    (incident?.files?.length ?? 0) === 1 ? '' : 's'
                  }`
            }
            tone={
              incident?.status === 'Awaiting Witness' ? 'warning' : undefined
            }
          />
        </Box>
      </Stack>
    </Paper>
  )
}

export default IncidentHero
